import React from 'react';
import { Link } from 'react-router-dom';
import type { MemberStats } from '../../api/families';
import { memberDisplayInitials, memberDisplayName } from '../../utils/memberDisplay';
import {
  formatAgeRo,
  formatDocumentsCountRo,
  formatRelativeTimeRo,
} from '../../utils/dates';
import { avatarGradient } from './memberAvatar';
import './MemberCard.css';

/**
 * Light-weight member shape accepted by `MemberCard`. Compatible with
 * both `FamilyMember` (from `families.ts`) and `SharedMemberItem`
 * (from `sharing.ts`) once we normalise the field names — the
 * sharing list uses `member_id` instead of `id` so the call site is
 * responsible for that mapping.
 */
export interface MemberCardMember {
  id: number;
  name: string;
  date_of_birth?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  member_relationship?: string | null;
  notes?: string | null;
  family_id?: number | null;
  family_name?: string | null;
  /** Loaded → render rich card. `undefined` → render skeleton chips.
   * `null` is treated like `undefined` (still loading / no payload). */
  stats?: MemberStats | null;
}

interface MemberCardProps {
  member: MemberCardMember;
  /** SharedMembers passes `true` → render the "Familie: …" line. */
  showFamily?: boolean;
  /** Override the default `/members/{id}` link target. */
  to?: string;
}

const genderLabel: Record<string, string> = {
  male: 'Masculin',
  female: 'Feminin',
  other: 'Altul',
};

const SELF_RELATIONSHIPS = new Set(['self', 'eu', 'eu însumi', 'eu insumi']);

const BADGE_VARIANTS = [
  'badge-teal',
  'badge-blue',
  'badge-amber',
  'badge-slate',
] as const;

/**
 * Tone the card lights up with:
 *  - `pending`: at least one document is in the OCR/summary/classification
 *               pipeline (animated amber strip).
 *  - `ok`:      member has documents AND no pending pipeline.
 *  - `empty`:   no documents — "new / waiting for first upload".
 *  - `loading`: stats payload not yet hydrated (treat as empty visually).
 */
type CardTone = 'pending' | 'ok' | 'empty' | 'loading';

function pickTone(stats: MemberStats | null | undefined): CardTone {
  if (!stats) return 'loading';
  if ((stats.pending_documents || 0) > 0) return 'pending';
  if ((stats.document_count || 0) > 0) return 'ok';
  return 'empty';
}

// Inline SVG icons. Kept here so we don't pull in an icon library
// and stay consistent with the existing `viewBox="0 0 24 24"` /
// stroke="currentColor" vocabulary used in `MemberDocumentCard`.
const FileIcon = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const ClockIcon = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const UsersIcon = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SpinnerIcon = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2v4" />
    <path d="m16.24 7.76 2.83-2.83" />
    <path d="M18 12h4" />
    <path d="m16.24 16.24 2.83 2.83" />
    <path d="M12 18v4" />
    <path d="m4.93 19.07 2.83-2.83" />
    <path d="M2 12h4" />
    <path d="m4.93 4.93 2.83 2.83" />
  </svg>
);

const TimelineIcon = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const SparkIcon = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3v3" />
    <path d="M12 18v3" />
    <path d="M3 12h3" />
    <path d="M18 12h3" />
    <path d="m5.6 5.6 2.1 2.1" />
    <path d="m16.3 16.3 2.1 2.1" />
    <path d="m5.6 18.4 2.1-2.1" />
    <path d="m16.3 7.7 2.1-2.1" />
  </svg>
);

function ageChip(dob?: string | null): string | null {
  return formatAgeRo(dob);
}

const MemberCard: React.FC<MemberCardProps> = ({ member, showFamily = false, to }) => {
  const tone = pickTone(member.stats);
  const stats = member.stats || null;
  const isLoading = tone === 'loading';

  const linkTarget = to || `/members/${member.id}`;
  const isSelf = Boolean(
    member.member_relationship &&
      SELF_RELATIONSHIPS.has(member.member_relationship.trim().toLowerCase()),
  );

  const ageLabel = ageChip(member.date_of_birth);
  const breakdown = stats?.document_type_breakdown || [];
  const otherTypes = stats?.document_type_other_count || 0;
  const lastActivityLabel = stats ? formatRelativeTimeRo(stats.last_activity_at) : null;

  return (
    <Link
      to={linkTarget}
      className={`member-card-v2-link member-card-v2-link--${tone}`}
      aria-label={`Deschide dosarul ${memberDisplayName(member)}`}
    >
      <div
        className={`card admin-card member-card-v2 member-card-v2--${tone}`}
        data-loading={isLoading ? 'true' : 'false'}
      >
        <div className="member-card-v2-accent" aria-hidden />

        {(stats?.has_timeline || stats?.has_insights) && (
          <div className="member-card-v2-indicators" aria-hidden>
            {stats?.has_timeline && (
              <span
                className="member-card-v2-indicator"
                title="Cronologie generată"
              >
                {TimelineIcon}
              </span>
            )}
            {stats?.has_insights && (
              <span
                className="member-card-v2-indicator"
                title="Are recomandări AI"
              >
                {SparkIcon}
              </span>
            )}
          </div>
        )}

        <div className="member-card-v2-head">
          <div
            className={`member-card-v2-avatar member-card-v2-avatar--${tone}`}
            style={{ background: avatarGradient(member.id) }}
            aria-hidden
          >
            {memberDisplayInitials(member)}
          </div>

          <div className="member-card-v2-titleblock">
            {showFamily && member.family_name && (
              <div className="member-card-v2-family">
                <span className="member-card-v2-family-label">Familie:</span>{' '}
                <span className="member-card-v2-family-name">{member.family_name}</span>
              </div>
            )}
            <div className="member-card-v2-name-row">
              <h3 className="member-card-v2-name">{memberDisplayName(member)}</h3>
              {isSelf && <span className="member-card-v2-self-badge">Tu</span>}
            </div>
            <div className="member-card-v2-chips">
              {member.member_relationship && (
                <span className="member-card-v2-chip member-card-v2-chip--relationship">
                  {member.member_relationship}
                </span>
              )}
              {member.gender && (
                <span className="member-card-v2-chip member-card-v2-chip--gender">
                  {genderLabel[member.gender] || member.gender}
                </span>
              )}
              {ageLabel && (
                <span className="member-card-v2-chip member-card-v2-chip--age">
                  {ageLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="member-card-v2-stats" role="group" aria-label="Statistici dosar">
          {isLoading ? (
            <>
              <span className="member-card-v2-stat member-card-v2-stat--skeleton" aria-hidden />
              <span className="member-card-v2-stat member-card-v2-stat--skeleton" aria-hidden />
            </>
          ) : (
            <>
              <span className="member-card-v2-stat" title="Numărul total de documente">
                {FileIcon}
                {formatDocumentsCountRo(stats?.document_count || 0)}
              </span>
              {lastActivityLabel && (
                <span className="member-card-v2-stat" title="Ultima activitate">
                  {ClockIcon}
                  {lastActivityLabel}
                </span>
              )}
              {stats && stats.pending_documents > 0 && (
                <span
                  className="member-card-v2-stat member-card-v2-stat--pending"
                  title="Documente în procesare (OCR, rezumat sau clasificare)"
                >
                  {SpinnerIcon}
                  {stats.pending_documents} în procesare
                </span>
              )}
              {stats && stats.shared_with_count > 0 && (
                <span
                  className="member-card-v2-stat member-card-v2-stat--shared"
                  title="Persoane cu acces partajat la acest membru"
                >
                  {UsersIcon}
                  Partajat cu {stats.shared_with_count}
                </span>
              )}
            </>
          )}
        </div>

        {isLoading ? (
          <div className="member-card-v2-types" aria-hidden>
            <span className="member-card-v2-type-skeleton" />
            <span className="member-card-v2-type-skeleton member-card-v2-type-skeleton--narrow" />
            <span className="member-card-v2-type-skeleton" />
          </div>
        ) : breakdown.length > 0 ? (
          <div className="member-card-v2-types">
            {breakdown.map((b, i) => (
              <span
                key={`${b.type}-${i}`}
                className={`badge ${BADGE_VARIANTS[i % BADGE_VARIANTS.length]} member-card-v2-type`}
                title={`${b.type} — ${b.count} ${b.count === 1 ? 'document' : 'documente'}`}
              >
                {b.type}
                <span className="member-card-v2-type-count">·{b.count}</span>
              </span>
            ))}
            {otherTypes > 0 && (
              <span
                className="badge badge-slate member-card-v2-type member-card-v2-type-more"
                title={`Încă ${otherTypes} ${otherTypes === 1 ? 'categorie' : 'categorii'} de documente`}
              >
                +{otherTypes}
              </span>
            )}
          </div>
        ) : (
          stats && (
            <div className="member-card-v2-types member-card-v2-types--empty">
              <span className="member-card-v2-empty-hint">
                Niciun document încărcat încă
              </span>
            </div>
          )
        )}
      </div>
    </Link>
  );
};

export default MemberCard;
